import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Form } from 'react-final-form';
import Bluebird from 'bluebird';
import queryString from 'query-string';
import {
  Box,
  Grid,
  Container,
  FormHelperText,
  Typography,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Capacitor } from '@capacitor/core';
import loginLogo from '../../assets/svg/new-logo-with-title-white.svg';
import { GoogleLogoIcon, AppleLogoIcon } from '../../assets/SvgIcons';
import routes from '../../routes';
import { DividerLine } from './DividerLine';
import { getPasswordsValidationErrors } from '../../helpers/validators';
import * as authService from '../../services/auth';
import * as authApi from '../../api/auth';
import * as usersApi from '../../api/users';
import snackbarHelper from '../../helpers/snackbarHelper';
import * as errorsHelper from '../../helpers/errors';
import { useFormStyles } from './authStyles';
import ButtonWithLoading from '../../components/ButtonWithLoading';
import errorMessages from '../../assets/errorMessages';
import { setFABActionsForCurrentlyLoggedInUser } from '../../services/auth';
import FABContext from '../../context/FABContext/FABContext';
import SocialMediaLinkIcons from '../../components/SocialMediaLinkIcons';
import RFFTextField from '../../components/Common/RFFTextField';

const validateForm = values => {
  const { email = '' } = values;
  const errors = getPasswordsValidationErrors(values, 'password1', 'password2');

  if (email.length === 0) {
    errors.email = 'Required';
  }

  return errors;
};

const RegisterPage = () => {
  const authStyles = useFormStyles();
  const history = useHistory();
  const location = useLocation();
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const isLoggingIn = useSelector(state => state.app.loggingIn);
  const [user, setUser] = useState(null);
  const fabContext = useContext(FABContext);
  const { formatMessage } = useIntl();
  // Apple sign-in is only offered in the native iOS app; Google is offered on all platforms.
  const isIos = Capacitor.getPlatform() === 'ios';

  // redirect to index if user is already logged in
  if (authService.getCurrentlyLoggedInUser()) {
    history.push(routes.index);
  }

  const parsedSearch = queryString.parse(location.search);
  const { invitation_hash } = parsedSearch;

  // this code is not suitable for an effect. effects run on component
  // mount and after EVERY update. this form isn't updated much, but logically
  // this just the wrong place for this code.
  useEffect(() => {
    if (!invitation_hash) {
      return;
    }

    setLoading(true);

    Bluebird.try(() => usersApi.getUserByInvitationHash(invitation_hash, null))
      .then(res => res.json())
      .then(res => {
        setLoading(false);

        const { user } = res;

        if (!user) {
          snackbarHelper.info('Invalid invitation code.');

          return false;
        }

        if (user.completed_registration === null) {
          return setUser(user);
        }

        snackbarHelper.info('This email address is already in use, please log in.');
      });
  }, [invitation_hash]);

  const onSubmit = values => {
    const { email, password1 } = values;

    setSubmitting(true);

    return Bluebird.try(() =>
      authApi.register(email, password1, user ? user.invitation_hash : null)
    )
      .then(res => res.json())
      .then(json => authService.handleAuthentication(history, json.data))
      .then(() => setFABActionsForCurrentlyLoggedInUser(fabContext))
      .catch(err => {
        setSubmitting(false);

        if (errorMessages.ERR_DUPLICATED_EMAIL.code === err.code) {
          snackbarHelper.info('This email address is already in use, please log in.');
        }

        return errorsHelper.handleError(err);
      });
  };

  const handleLoginWithGoogle = () => {
    let options = {};

    if (user) {
      options.invitation_hash = user.invitation_hash;
    }

    return authService.authorizeWithGoogle(options, fabContext);
  };

  const handleLoginWithApple = () => {
    let options = {};

    if (user) {
      options.invitation_hash = user.invitation_hash;
    }

    return authService.authorizeWithApple(options, fabContext);
  };

  const handleSignUpUsingDifferentEmailClick = e => {
    e.preventDefault();

    setUser(null);

    history.push(routes.signup);
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Box sx={authStyles.pageBackground}>
      <Container maxWidth="md">
        <Grid container spacing={2} sx={authStyles.pageContainer} data-testid="welcome-index-page">
          <Grid item xs sx={authStyles.logoWrapper}>
            <Box component="img" src={loginLogo} alt="logo" sx={authStyles.logo} />
          </Grid>
          <Grid item xs sx={authStyles.loginFormWrapper}>
            <Typography sx={authStyles.headerText} data-testid="signup-header">
              {user
                ? `${formatMessage({ id: 'signup.header.hello' })}, ${user.firstname}!`
                : formatMessage({ id: 'signup.header.sign-up' })}
            </Typography>

            <Form
              onSubmit={onSubmit}
              validate={validateForm}
              initialValues={{
                email: user ? user.email : '',
              }}
              render={({ handleSubmit, submitting, pristine, invalid, dirtySinceLastSubmit }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    <Box sx={authStyles.input}>
                      <RFFTextField
                        margin="normal"
                        name="email"
                        type="email"
                        variant="outlined"
                        fullWidth
                        placeholder={user ? 'Claim Profile' : formatMessage({ id: 'signup.form.email-label' })}
                        autoComplete="email"
                        data-testid="register-email-input"
                        disabled={user !== null || isLoggingIn}
                      />
                    </Box>

                    <Box sx={[{ marginBottom: 0 }, authStyles.input]}>
                      <RFFTextField
                        margin="normal"
                        name="password1"
                        disabled={isLoggingIn}
                        type={showPassword ? 'text' : 'password'}
                        variant="outlined"
                        fullWidth
                        placeholder={formatMessage({ id: 'signup.form.password-label' })}
                        autoComplete="new-password"
                        data-testid="register-password-input"
                        InputProps={{
                          endAdornment: (
                            <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                              <InputAdornment position="end">
                                <IconButton onClick={handleClickShowPassword} size="large">
                                  {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </InputAdornment>
                            </Tooltip>
                          ),
                        }}
                      />
                    </Box>
                    <FormHelperText style={authStyles.helpText}>
                      {formatMessage({ id: 'signup.form.password-helper-text' })}
                    </FormHelperText>

                    <Box sx={authStyles.input}>
                      <RFFTextField
                        margin="normal"
                        name="password2"
                        disabled={isLoggingIn}
                        type={showPassword ? 'text' : 'password'}
                        variant="outlined"
                        fullWidth
                        placeholder={formatMessage({ id: 'signup.form.password-repeat-label' })}
                        autoComplete="new-password"
                        data-testid="register-password-confirmation-input"
                        InputProps={{
                          endAdornment: (
                          <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                            <InputAdornment position="end">
                              <IconButton onClick={handleClickShowPassword} size="large">
                                {showPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          </Tooltip>
                          ),
                        }}
                      />
                    </Box>

                    <ButtonWithLoading
                      sx={authStyles.submitButton}
                      fullWidth
                      type="submit"
                      isLoading={submitting || isSubmitting || isLoggingIn}
                      disabled={pristine || (invalid && !dirtySinceLastSubmit) || isLoggingIn}
                      data-testid="register-submit-button"
                    >
                      {formatMessage({ id: 'signup.form.submit-sign-up' })}
                    </ButtonWithLoading>
                  </form>
                );
              }}
            />

            <DividerLine />

            {isIos && (
              <ButtonWithLoading
                sx={authStyles.appleButton}
                startIcon={<AppleLogoIcon width={36} height={36} />}
                isLoading={isSubmitting || isLoggingIn}
                disabled={isSubmitting || isLoggingIn}
                fullWidth
                type="submit"
                onClick={() => handleLoginWithApple()}
              >
                {formatMessage({ id: 'signup.form.sign-up-with-apple' })}
              </ButtonWithLoading>
            )}

            <ButtonWithLoading
              sx={authStyles.googleButton}
              startIcon={<GoogleLogoIcon width={36} height={36} />}
              isLoading={isSubmitting || isLoggingIn}
              disabled={isSubmitting || isLoggingIn}
              fullWidth
              type="submit"
              onClick={() => handleLoginWithGoogle()}
            >
              {formatMessage({ id: 'signup.form.sign-up-with-google' })}
            </ButtonWithLoading>

            <Grid
              container
              sx={(theme) => ({
                [theme.breakpoints.down('sm')]: {
                  flexDirection: 'column',
                  gap: 1,
                  marginTop: 1,
                },
              })}
            >
              <Grid item>
                <Link to={routes.login} variant="body2" style={{ color: '#fff' }}>
                  {formatMessage({ id: 'signup.form.have-account' })}
                </Link>
              </Grid>
              {user && (
                <Grid item>
                  <Link to="#" style={{ color: '#fff' }} onClick={handleSignUpUsingDifferentEmailClick}>
                    Sign up using a different email address
                  </Link>
                </Grid>
              )}
            </Grid>

            <Grid container direction="row" justifyContent="center" alignItems="center" sx={{ paddingTop: 2 }}>
              <SocialMediaLinkIcons iconColor="#fff" />
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default RegisterPage;
